'use client'
import { useState } from 'react'
import { consumerRequest, generateAESKey } from '@/helper/request'
import {
  FE_AUTH_FORCE_LOGOUT_API,
  FE_AUTH_VALIDATE_API,
  LOGIN_API,
} from '@/helper/apiConstants'
import { dispatch, useSelector } from '@/lib/store'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import { updateUserProfile } from '@/lib/reducers/userReducer'
import { saveState } from '@/utils/localStorage'
import useCustomToast from '@/hooks/useCustomToast'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { deepClone, getAllPrimaryProfiles, isEmpty } from '@/utils/commonFunc'
import { decryptAES, encryptAES } from '@/utils/forgeencryption'
import { askUserToSelectPatient } from '@/lib/reducers/patientReducer'
import { updateEventUser } from '@/utils/events'
import { CONSUMER_WEB_PLATFORM, CURRENT_SUBSIDIARY } from '@/utils/constants'

interface UseLoginOtpReturn {
  loading: boolean
  error: string | null
  setError: (error: string | null) => void
  handleResendOTP: () => Promise<void>
  handleSubmitOTP: (otpInput: string[]) => Promise<void>
  handleEditPhoneNumber: () => void
  resetCount: boolean
}

const useLoginOtp = (): UseLoginOtpReturn => {
  const showToast = useCustomToast()

  const loginState = useSelector((state) => state?.login) || {}
  const commonState = useSelector((state) => state.common) || {}
  const patientState = useSelector((state) => state.patient) || {}

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetCount, setResetCount] = useState(false)

  const handleResendOTP = async () => {
    const formData = {
      phoneNumber: loginState?.phoneNumber,
      subsidiary: CURRENT_SUBSIDIARY,
    }
    setLoading(true)
    setError(null)
    const apiRes: any = await consumerRequest(LOGIN_API, 'post', formData)
    if (apiRes?.status === true) {
      showToast('success', 'OTP has been sent')
      setResetCount(true)
      setTimeout(() => setResetCount(false), 100)
    } else {
      showToast('danger', 'Failed to send OTP')
    }
    setLoading(false)
  }

  const handleSubmitOTP = async (otpInput: string[]) => {
    const aesKey = generateAESKey()
    const formData = {
      otp: otpInput.join(''),
      phoneNumber: loginState?.phoneNumber,
    }
    const payload = encryptAES(formData, aesKey)
    setLoading(true)

    const apiRes: any = await fetch(FE_AUTH_VALIDATE_API, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        app: CONSUMER_WEB_PLATFORM,
        'X-Frame-Options': 'DENY',
        'Strict-Transport-Security':
          'max-age=31536000; includeSubDomains; preload',
        'X-Content-Type-Options': 'nosniff',
        'Referrer-Policy': 'strict-origin-when-cross-origin',
      },
    })

    let res = await apiRes?.json()
    if (res?.token) {
      saveState('uidx', res.token)
    } else {
      console.error('No token found in validate response')
    }

    try {
      res = { ...res, data: decryptAES(res?.data, aesKey) }
    } catch {
      res = { data: null, message: res?.message, status: false }
    }

    if (res?.status === false) {
      setError(res?.message)
      setLoading(false)
      return
    }

    if (!!res?.status) {
      if (res?.data?.sessionExists) {
        const logoutRes: any = await handleForceLogoutUser(otpInput)
        if (!!logoutRes.status) {
          dispatch(
            updateLoginData({
              ...loginState,
              loginModalIsOpen: false,
              activeStep: 1,
            })
          )
        }
      }
      if (
        !res?.data?.sessionExists &&
        res?.data?.patientDetailDtoList?.length > 0
      ) {
        await handleSaveUser(res?.data)
      }
      if (
        !res?.data?.sessionExists &&
        (res?.data?.patientDetailDtoList?.length === 0 ||
          res?.data?.patientDetailDtoList === null)
      ) {
        if (res?.data?.secureDeviceDto?.abcAxis)
          saveState('abcAxis', res?.data?.secureDeviceDto?.abcAxis)
        dispatch(updateLoginData({ ...loginState, loginModalIsOpen: false }))
        dispatch(
          updateCommonReducerData({
            ...commonState,
            userOnboardingModalIsOpen: true,
          })
        )
      }
    }
    window.scrollTo({ top: 0, behavior: 'auto' })
    setLoading(false)
  }

  const handleForceLogoutUser = async (otpInput: string[]) => {
    const aesKey = generateAESKey()
    const formData = {
      otp: otpInput.join(''),
      phoneNumber: loginState?.phoneNumber,
    }
    const payload = encryptAES(formData, aesKey)
    setLoading(true)

    const apiRes: any = await fetch(FE_AUTH_FORCE_LOGOUT_API, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        app: CONSUMER_WEB_PLATFORM,
        'X-Frame-Options': 'DENY',
        'Strict-Transport-Security':
          'max-age=31536000; includeSubDomains; preload',
        'X-Content-Type-Options': 'nosniff',
        'Referrer-Policy': 'strict-origin-when-cross-origin',
      },
    })

    let res = await apiRes?.json()
    if (res?.token) {
      saveState('uidx', res.token)
    } else {
      console.error('No token found in force logout response')
    }

    try {
      res = { ...res, data: decryptAES(res?.data, aesKey) }
    } catch {
      res = { data: null, message: res?.message, status: false }
    }

    if (
      res?.data?.patientDetailDtoList?.length === 0 ||
      res?.data?.patientDetailDtoList === null
    ) {
      dispatch(
        updateCommonReducerData({
          ...commonState,
          userOnboardingModalIsOpen: true,
        })
      )
    } else {
      await handleSaveUser(res?.data)
    }
    setLoading(false)
    return res
  }

  const handleSaveUser = async (data: any) => {
    const members = data?.patientDetailDtoList || []
    const phone = loginState?.phoneNumber

    const primaryProfiles = getAllPrimaryProfiles(members)

    if (
      !isEmpty(members) &&
      (isEmpty(primaryProfiles) || primaryProfiles?.length > 1)
    )
      dispatch(
        askUserToSelectPatient({
          ...patientState,
          selectedTest: {},
          selectPatientModalIsOpen: true,
          loginFlowPrimarySelection: true,
        })
      )

    const selfUser = members?.find(
      (item: any) => item.relation === 'SELF' && item.phoneNumber === phone
    )

    const fallbackUser = members?.find(
      (item: any) => item.phoneNumber === phone
    )

    const userObj =
      primaryProfiles?.[0] || selfUser || fallbackUser || members?.[0] || {}

    const user = {
      firstName: userObj.firstName,
      lastName: userObj.lastName,
      dob: userObj?.dob,
      phoneNumber: phone,
      gender: userObj?.gender,
      code: userObj?.code,
      relation: userObj?.relation,
    }

    saveState('user', user)
    saveState('ctxVal', phone)
    updateEventUser(user)

    if (data?.secureDeviceDto?.abcAxis) {
      saveState('abcAxis', data.secureDeviceDto.abcAxis)
    }

    dispatch(updateUserProfile({ ...user, membersList: members }))

    const updatedLoginState = {
      ...deepClone(loginState),
      isUserLoggedIn: true,
      loginModalIsOpen: false,
      activeStep: 1,
    }

    dispatch(updateLoginData(updatedLoginState))
  }

  const handleEditPhoneNumber = () => {
    dispatch(
      updateLoginData({
        ...loginState,
        hasOTPSent: false,
        activeStep: 1,
      })
    )
  }

  return {
    loading,
    error,
    setError,
    handleResendOTP,
    handleSubmitOTP,
    handleEditPhoneNumber,
    resetCount,
  }
}

export default useLoginOtp
